/**
 * Nosto-PlpAdvertisementManager.js
 *
 * Administra y rota hasta diez banners dentro del grid del PLP.
 *
 * El HTML del anuncio se obtiene desde un <template> renderizado por Liquid.
 * El anuncio:
 * - solo aparece en la vista normal;
 * - se inserta después del número de producto configurado;
 * - puede ocupar una o dos columnas en desktop;
 * - se elimina en editorial;
 * - se vuelve a colocar después de reemplazar o agregar resultados.
 */

(function initializeNostoPlpAdvertisementManager(global) {
    "use strict";

    class NostoPlpAdvertisementManager {
        constructor({
            root = null,
            container = null,
            template = null,
            templateSelector =
            "[data-nosto-plp-ad-template]",
            productSelector =
            "[data-nosto-product-id][data-nosto-rendered='true']",
            debug = false,
        } = {}) {
            this.root =
                this.resolveElement(root);

            this.container =
                this.resolveElement(container);

            this.template =
                this.resolveElement(template) ||
                this.root?.querySelector(
                    templateSelector
                ) ||
                document.querySelector(
                    templateSelector
                );

            this.productSelector =
                String(
                    productSelector || ""
                ).trim();

            this.debug =
                Boolean(debug);

            this.position =
                this.normalizePositiveInteger(
                    this.template?.dataset
                        .position,
                    1
                );

            /**
             * Cantidad de productos que conforman cada carga normal del PLP.
             * En el template normal este valor proviene de data-nosto-page-size.
             */
            this.batchSize =
                this.normalizePositiveInteger(
                    this.template?.dataset
                        .batchSize ||
                    this.root?.dataset
                        .nostoPageSize,
                    24
                );

            this.currentViewMode =
                "normal";
        }

        log(...args) {
            if (!this.debug) return;

            console.info(
                "[NostoPlpAdvertisementManager]",
                ...args
            );
        }

        resolveElement(target) {
            if (target instanceof Element) {
                return target;
            }

            if (
                typeof target ===
                "string" &&
                target.trim()
            ) {
                return document.querySelector(
                    target
                );
            }

            return null;
        }

        normalizePositiveInteger(
            value,
            fallback = 1
        ) {
            const number =
                Number(value);

            if (
                !Number.isFinite(number) ||
                number < 1
            ) {
                return fallback;
            }

            return Math.floor(number);
        }

        normalizeColumnSpan(value) {
            return String(value) === "2"
                ? 2
                : 1;
        }

        isEnabled() {
            return Boolean(
                this.root &&
                this.container &&
                this.template &&
                this.template.content
            );
        }

        getAdvertisements() {
            if (!this.container) {
                return [];
            }

            return Array.from(
                this.container.querySelectorAll(
                    "[data-nosto-plp-ad]"
                )
            );
        }

        /**
         * Mantiene compatibilidad con consumidores que esperen un solo anuncio.
         */
        getAdvertisement() {
            return (
                this.getAdvertisements()[0] ||
                null
            );
        }

        getRenderedProducts() {
            if (
                !this.container ||
                !this.productSelector
            ) {
                return [];
            }

            return Array.from(
                this.container.querySelectorAll(
                    this.productSelector
                )
            );
        }

        remove() {
            const advertisements =
                this.getAdvertisements();

            advertisements.forEach(
                (advertisement) =>
                    advertisement.remove()
            );

            return advertisements.length;
        }

        /**
         * Devuelve los banners configurados dentro del template Liquid.
         * Los campos vacíos no generan nodos, así que la lista contiene
         * únicamente imágenes disponibles.
         */
        getAdvertisementSources() {
            if (
                !this.template?.content
            ) {
                return [];
            }

            return Array.from(
                this.template.content
                    .querySelectorAll(
                        "[data-nosto-plp-ad-source]"
                    )
            );
        }

        /**
 * Crea una copia del banner correspondiente al índice de repetición.
 *
 * Si hay menos de diez banners configurados, rota exclusivamente
 * los que realmente existen.
 *
 * Cada banner conserva su propio column span.
 */
        createAdvertisement(
            repeatIndex = 0
        ) {
            if (!this.isEnabled()) {
                return null;
            }

            const sources =
                this.getAdvertisementSources();

            if (!sources.length) {
                return null;
            }

            const normalizedRepeatIndex =
                Math.max(
                    0,
                    Number(repeatIndex) || 0
                );

            /*
             * Ejemplo con 3 banners:
             *
             * 0 -> banner 1
             * 1 -> banner 2
             * 2 -> banner 3
             * 3 -> banner 1
             * 4 -> banner 2
             */
            const sourceIndex =
                normalizedRepeatIndex %
                sources.length;

            const source =
                sources[sourceIndex];

            const columnSpan =
                this.normalizeColumnSpan(
                    source.dataset.columnSpan
                );

            const advertisement =
                source.cloneNode(true);

            advertisement.removeAttribute(
                "data-nosto-plp-ad-source"
            );

            advertisement.setAttribute(
                "data-nosto-plp-ad",
                ""
            );

            advertisement.dataset
                .bannerIndex =
                String(sourceIndex);

            advertisement.dataset
                .columnSpan =
                String(columnSpan);

            advertisement.style.setProperty(
                "--plp-ad-column-span",
                String(columnSpan)
            );

            const fragment =
                document.createDocumentFragment();

            fragment.appendChild(
                advertisement
            );

            return {
                fragment,
                advertisement,
                sourceIndex,
                sourceCount:
                    sources.length,
                columnSpan,
            };
        }

        /**
         * Devuelve los índices globales después de los cuales debe aparecer
         * publicidad dentro de los productos actualmente renderizados.
         *
         * Ejemplo:
         * position = 4, batchSize = 24
         * → índices base cero: 3, 27, 51, 75...
         */
        getAdvertisementTargetIndexes(
            productCount
        ) {
            const indexes = [];

            for (
                let index =
                    this.position - 1;
                index < productCount;
                index += this.batchSize
            ) {
                indexes.push(index);
            }

            return indexes;
        }

        /**
         * Sincroniza todos los anuncios con el modo actual y los productos
         * renderizados. En vista normal inserta una copia por cada carga.
         */
        sync(viewMode = "normal") {
            this.currentViewMode =
                String(
                    viewMode || "normal"
                )
                    .trim()
                    .toLowerCase();

            this.remove();

            if (
                !this.isEnabled() ||
                this.currentViewMode !==
                "normal"
            ) {
                return [];
            }

            const products =
                this.getRenderedProducts();

            if (
                products.length <
                this.position
            ) {
                this.log(
                    "Aún no existen suficientes productos para insertar publicidad.",
                    {
                        position:
                            this.position,
                        products:
                            products.length,
                    }
                );

                return [];
            }

            const targetIndexes =
                this.getAdvertisementTargetIndexes(
                    products.length
                );

            const insertedAdvertisements = [];

            targetIndexes.forEach(
                (
                    targetIndex,
                    repeatIndex
                ) => {
                    const targetProduct =
                        products[targetIndex];

                    const created =
                        this.createAdvertisement(
                            repeatIndex
                        );

                    if (
                        !targetProduct ||
                        !created
                    ) {
                        return;
                    }

                    created.advertisement.dataset
                        .repeatIndex =
                        String(repeatIndex);

                    created.advertisement.dataset
                        .afterProduct =
                        String(targetIndex + 1);

                    created.advertisement.dataset
                        .bannerNumber =
                        created.advertisement.dataset
                            .bannerNumber ||
                        String(
                            created.sourceIndex + 1
                        );

                    created.advertisement.dataset
                        .bannerCount =
                        String(
                            created.sourceCount
                        );

                    targetProduct.after(
                        created.fragment
                    );

                    insertedAdvertisements.push(
                        created.advertisement
                    );
                }
            );

            this.log(
                "Publicidad sincronizada.",
                {
                    position:
                        this.position,

                    batchSize:
                        this.batchSize,

                    products:
                        products.length,

                    inserted:
                        insertedAdvertisements.length,

                    availableBanners:
                        this.getAdvertisementSources()
                            .length,

                    columnSpans:
                        insertedAdvertisements.map(
                            (advertisement) =>
                                advertisement.dataset
                                    .columnSpan
                        ),
                }
            );

            return insertedAdvertisements;
        }

        destroy() {
            this.remove();

            this.root = null;
            this.container = null;
            this.template = null;
        }
    }

    global.NostoPlpAdvertisementManager =
        NostoPlpAdvertisementManager;
})(window);
